import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Tabs, Select, Tooltip } from 'antd';
import {
  BarChartOutlined, DollarOutlined, SendOutlined,
  DownloadOutlined, PrinterOutlined, ReloadOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';

import TabSinistres, { exportSinistres } from './reporting/TabSinistres';
import TabEncaissements, { exportEncaissements } from './reporting/TabEncaissements';
import TabPaiements, { exportPaiements } from './reporting/TabPaiements';
import TabCadence, { exportCadence } from './reporting/TabCadence';
import TabCadenceEncaissements, { exportCadenceEncaissements } from './reporting/TabCadenceEncaissements';
import TableExportButton from '../../components/TableExportButton';
import PageHeader from '../../components/PageHeader';
import logoLocal from '../../assets/images/logo.png';

// ── Données statiques ─────────────────────────────────────────────
// Index 1 à 12, dans la langue de l'interface (lu au rendu).
const libellesMois = (t) => ['', ...Array.from({ length: 12 }, (_, i) => t(`commun.mois.${i + 1}`))];
const ANNEES = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
const G = '#1a6b3c';
const INK = '#0f172a';
const MUTED = '#94a3b8';
const BORDER = '#e2e8f0';
const WHITE = '#ffffff';

// ── Clé de cache : (annee, mois) unique par sélection ────────────
const cacheKey = (annee, mois) => `${annee}-${mois}`;

// ── Colonnes communes parPays/parCompagnie (sinistres / enc / paiements) ──
function colsReportingComparatif(labelCol, n, n1, t) {
  return [
    { title: labelCol, dataIndex: '_label' },
    { title: t('reporting.colonnes.teMois', { annee: n1 }), dataIndex: 'te_mois_n1', numeric: true },
    { title: t('reporting.colonnes.teMois', { annee: n }), dataIndex: 'te_mois_n', numeric: true },
    { title: t('reporting.colonnes.etMois', { annee: n1 }), dataIndex: 'et_mois_n1', numeric: true },
    { title: t('reporting.colonnes.etMois', { annee: n }), dataIndex: 'et_mois_n', numeric: true },
    { title: t('reporting.colonnes.totalMois', { annee: n1 }), dataIndex: 'tot_mois_n1', numeric: true },
    { title: t('reporting.colonnes.totalMois', { annee: n }), dataIndex: 'tot_mois_n', numeric: true },
    { title: t('reporting.colonnes.teCumul', { annee: n1 }), dataIndex: 'te_cumul_n1', numeric: true },
    { title: t('reporting.colonnes.teCumul', { annee: n }), dataIndex: 'te_cumul_n', numeric: true },
    { title: t('reporting.colonnes.etCumul', { annee: n1 }), dataIndex: 'et_cumul_n1', numeric: true },
    { title: t('reporting.colonnes.etCumul', { annee: n }), dataIndex: 'et_cumul_n', numeric: true },
    { title: t('reporting.colonnes.totalCumul', { annee: n1 }), dataIndex: 'tot_cumul_n1', numeric: true },
    { title: t('reporting.colonnes.totalCumul', { annee: n }), dataIndex: 'tot_cumul_n', numeric: true },
    { title: t('reporting.colonnes.finAnnee', { annee: n }), dataIndex: 'tot_fda_n', numeric: true },
  ];
}

// ── Colonnes comparatives (nb/montant) pour Encaissements / Paiements ──
// (données au format TableauFinancier : nb_*/mt_*, pas TE/ET comme Sinistres)
function colsReportingFinancier(labelCol, n, n1, t) {
  return [
    { title: labelCol, dataIndex: '_label' },
    { title: t('reporting.colonnes.nbMois', { annee: n1 }), dataIndex: 'nb_mois_n1', numeric: true },
    { title: t('reporting.colonnes.montantMois', { annee: n1 }), dataIndex: 'mt_mois_n1', format: 'currency' },
    { title: t('reporting.colonnes.nbMois', { annee: n }), dataIndex: 'nb_mois_n', numeric: true },
    { title: t('reporting.colonnes.montantMois', { annee: n }), dataIndex: 'mt_mois_n', format: 'currency' },
    { title: t('reporting.colonnes.nbCumul', { annee: n1 }), dataIndex: 'nb_cumul_n1', numeric: true },
    { title: t('reporting.colonnes.montantCumul', { annee: n1 }), dataIndex: 'mt_cumul_n1', format: 'currency' },
    { title: t('reporting.colonnes.nbCumul', { annee: n }), dataIndex: 'nb_cumul_n', numeric: true },
    { title: t('reporting.colonnes.montantCumul', { annee: n }), dataIndex: 'mt_cumul_n', format: 'currency' },
    { title: t('reporting.colonnes.nbFinAnnee', { annee: n }), dataIndex: 'nb_fda_n', numeric: true },
    { title: t('reporting.colonnes.montantFinAnnee', { annee: n }), dataIndex: 'mt_fda_n', format: 'currency' },
  ];
}

function flattenReportingTab(dataTab, labelKeyPays, labelKeyComp) {
  if (!dataTab) return [];
  const rows = [];
  (dataTab.parPays || []).forEach(l => rows.push({ ...l, _label: l[labelKeyPays] || l.pays }));
  if (dataTab.totalPays) rows.push({ ...dataTab.totalPays, _label: i18n.t(`statistiques:reporting.totaux.${labelKeyPays}`) });
  (dataTab.parCompagnie || []).forEach(l => rows.push({ ...l, _label: l[labelKeyComp] || l.compagnie }));
  if (dataTab.totalCompagnie) rows.push({ ...dataTab.totalCompagnie, _label: i18n.t('statistiques:reporting.totaux.compagnies') });
  return rows;
}

function buildExportForTab(activeTab, dataSin, dataEnc, dataPay, dataCad, dataCadEnc, annee) {
  const t = (cle, options) => i18n.t(`statistiques:${cle}`, options);
  const n = annee, n1 = annee - 1;
  if (activeTab === 'sinistres' && dataSin) {
    return {
      data: flattenReportingTab(dataSin, 'pays', 'compagnie'),
      columns: colsReportingComparatif(t('reporting.paysCompagnie'), n, n1, t),
      title: t('reporting.export.sinistres'),
    };
  }
  if (activeTab === 'encaissements' && dataEnc) {
    return {
      data: flattenReportingTab(dataEnc, 'pays', 'compagnie'),
      columns: colsReportingFinancier(t('reporting.paysCompagnie'), n, n1, t),
      title: t('reporting.export.encaissements'),
    };
  }
  if (activeTab === 'paiements' && dataPay) {
    return {
      data: flattenReportingTab(dataPay, 'pays', 'compagnie'),
      columns: colsReportingFinancier(t('reporting.paysCompagnie'), n, n1, t),
      title: t('reporting.export.paiements'),
    };
  }
  if (activeTab === 'cadence' && dataCad) {
    // Cadence paiements : lignes indexées par exercice de survenance,
    // cellules = { [exercicePaiement]: { nb, montant } } (cf. TabCadence.js)
    const anneeRefCad = dataCad.anneeRef ?? n;
    const anneeMinCad = anneeRefCad - 3;
    const labelExercice = (a) => (a === -1 ? t('commun.etAnterieures', { annee: anneeMinCad - 1 }) : String(a));
    const lignes = (dataCad.total?.lignes || []).map(l => ({ ...l, _label: labelExercice(l.anneeSurvenance) }));
    const ep = dataCad.anneesColonnes || [];
    const cols = [
      { title: t('reporting.colonnes.exercice'), dataIndex: '_label' },
      ...ep.map(e => ({ title: t('reporting.colonnes.nbPayes', { annee: labelExercice(e) }), dataIndex: `_ep_nb_${e}`, numeric: true,
        export: (_v, r) => {
          const c = r.cellules?.[e];
          return c?.nb ? Number(c.nb).toLocaleString(localeIntl()) : '';
        }
      })),
      { title: t('reporting.colonnes.totalNb'), dataIndex: 'nbTotal', numeric: true },
      { title: t('reporting.colonnes.declares'), dataIndex: 'sinistresDeClares', numeric: true },
      { title: t('reporting.colonnes.totalMontant'), dataIndex: 'montantTotal', format: 'currency' },
    ];
    return { data: lignes, columns: cols, title: t('reporting.export.cadencePaiements') };
  }
  if (activeTab === 'cadence-enc' && dataCadEnc) {
    const anneeRefCE = dataCadEnc.anneeRef ?? n;
    const anneeMinCE = anneeRefCE - 3;
    const labelExerciceCE = (a) => (a === -1 ? t('commun.etAnterieures', { annee: anneeMinCE - 1 }) : String(a));
    const lignes = (dataCadEnc.total?.lignes || []).map(l => ({ ...l, _label: labelExerciceCE(l.anneeSurvenance) }));
    const cols = [
      { title: t('reporting.colonnes.exercice'), dataIndex: '_label' },
      { title: t('reporting.colonnes.totalNb'), dataIndex: 'nbTotal', numeric: true },
      { title: t('reporting.colonnes.totalMontant'), dataIndex: 'montantTotal', format: 'currency' },
    ];
    return { data: lignes, columns: cols, title: t('reporting.export.cadenceEncaissements') };
  }
  return { data: [], columns: [], title: t('reporting.export.titre') };
}

// ══════════════════════════════════════════════════════════════════
export default function ReportingMensuel() {
  const { t } = useTranslation('statistiques');
  const MOIS_FR = libellesMois(t);
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [mois, setMois] = useState(new Date().getMonth() + 1);
  const [activeTab, setActiveTab] = useState('sinistres');

  // Cache par onglet : { "2025-3": data }
  const cacheSin = useRef({});
  const cacheEnc = useRef({});
  const cachePay = useRef({});

  // État de chargement et données courantes
  const [dataSin, setDataSin] = useState(null);
  const [dataEnc, setDataEnc] = useState(null);
  const [dataPay, setDataPay] = useState(null);
  const [dataCad, setDataCad] = useState(null);
  const [dataCadEnc, setDataCadEnc] = useState(null);
  const [loadingSin, setLoadingSin] = useState(false);
  const [loadingEnc, setLoadingEnc] = useState(false);
  const [loadingPay, setLoadingPay] = useState(false);
  const [loadingCad, setLoadingCad] = useState(false);
  const [loadingCadEnc, setLoadingCadEnc] = useState(false);

  const cacheCad = useRef({});
  const cacheCadEnc = useRef({});

  // Onglets déjà chargés (pour lazy loading)
  const visitedTabs = useRef(new Set(['sinistres']));

  // ── Chargement avec cache ──────────────────────────────────────
  const chargerSinistres = useCallback(async (a, m, force = false) => {
    const key = cacheKey(a, m);
    if (!force && cacheSin.current[key]) {
      setDataSin(cacheSin.current[key]);
      return;
    }
    setLoadingSin(true);
    try {
      const r = await axiosInstance.get(`/v1/stats/reporting-mensuel?annee=${a}&mois=${m}`);
      cacheSin.current[key] = r.data?.data;
      setDataSin(r.data?.data);
    } catch { toast.error(t('reporting.erreurs.sinistres')); }
    setLoadingSin(false);
  }, [t]);

  const chargerEncaissements = useCallback(async (a, m, force = false) => {
    const key = cacheKey(a, m);
    if (!force && cacheEnc.current[key]) {
      setDataEnc(cacheEnc.current[key]);
      return;
    }
    setLoadingEnc(true);
    try {
      const r = await axiosInstance.get(`/v1/stats/reporting-encaissements?annee=${a}&mois=${m}`);
      cacheEnc.current[key] = r.data?.data;
      setDataEnc(r.data?.data);
    } catch { toast.error(t('reporting.erreurs.encaissements')); }
    setLoadingEnc(false);
  }, [t]);

  const chargerPaiements = useCallback(async (a, m, force = false) => {
    const key = cacheKey(a, m);
    if (!force && cachePay.current[key]) {
      setDataPay(cachePay.current[key]);
      return;
    }
    setLoadingPay(true);
    try {
      const r = await axiosInstance.get(`/v1/stats/reporting-paiements?annee=${a}&mois=${m}`);
      cachePay.current[key] = r.data?.data;
      setDataPay(r.data?.data);
    } catch (e) {
      // Endpoint pas encore disponible → pas d'erreur toast
      if (e?.response?.status !== 404) toast.error(t('reporting.erreurs.paiements'));
    }
    setLoadingPay(false);
  }, [t]);

  const chargerCadence = useCallback(async (a, force = false) => {
    const key = String(a);
    if (!force && cacheCad.current[key]) { setDataCad(cacheCad.current[key]); return; }
    setLoadingCad(true);
    try {
      const r = await axiosInstance.get(`/v1/stats/cadence?annee=${a}`);
      cacheCad.current[key] = r.data?.data;
      setDataCad(r.data?.data);
    } catch { toast.error(t('reporting.erreurs.cadence')); }
    setLoadingCad(false);
  }, [t]);

  const chargerCadenceEnc = useCallback(async (a, force = false) => {
    const key = String(a);
    if (!force && cacheCadEnc.current[key]) { setDataCadEnc(cacheCadEnc.current[key]); return; }
    setLoadingCadEnc(true);
    try {
      const r = await axiosInstance.get(`/v1/stats/cadence-encaissements?annee=${a}`);
      cacheCadEnc.current[key] = r.data?.data;
      setDataCadEnc(r.data?.data);
    } catch { toast.error(t('reporting.erreurs.cadenceEncaissements')); }
    setLoadingCadEnc(false);
  }, [t]);
  useEffect(() => {
    chargerSinistres(annee, mois);
  }, [annee, mois, chargerSinistres]);

  // ── Changement mois/année → invalider cache + recharger actif ──
  const onPeriodeChange = (newAnnee, newMois) => {
    // Vider les données affichées pour feedback immédiat
    setDataSin(null); setDataEnc(null); setDataPay(null);
    // Invalider le cache pour la nouvelle période
    // (les anciennes périodes restent en cache)
    chargerSinistres(newAnnee, newMois);
    if (visitedTabs.current.has('encaissements'))
      chargerEncaissements(newAnnee, newMois);
    if (visitedTabs.current.has('paiements'))
      chargerPaiements(newAnnee, newMois);
    if (visitedTabs.current.has('cadence')) chargerCadence(newAnnee);
    if (visitedTabs.current.has('cadence-enc')) chargerCadenceEnc(newAnnee);
  };

  const setAnneeM = (v) => { setAnnee(v); onPeriodeChange(v, mois); };
  const setMoisM = (v) => { setMois(v); onPeriodeChange(annee, v); };

  // ── Changement d'onglet → lazy loading ────────────────────────
  const onTabChange = (tab) => {
    setActiveTab(tab);
    if (!visitedTabs.current.has(tab)) {
      visitedTabs.current.add(tab);
      if (tab === 'encaissements') chargerEncaissements(annee, mois);
      if (tab === 'paiements') chargerPaiements(annee, mois);
      if (tab === 'cadence') chargerCadence(annee);
      if (tab === 'cadence-enc') chargerCadenceEnc(annee);
    }
  };

  // ── Actualiser l'onglet actif ─────────────────────────────────
  const actualiser = () => {
    if (activeTab === 'sinistres') chargerSinistres(annee, mois, true);
    if (activeTab === 'encaissements') chargerEncaissements(annee, mois, true);
    if (activeTab === 'paiements') chargerPaiements(annee, mois, true);
    if (activeTab === 'cadence') chargerCadence(annee, true);
    if (activeTab === 'cadence-enc') chargerCadenceEnc(annee, true);
  };

  // ── Export selon onglet actif ─────────────────────────────────
  const exporter = () => {
    if (activeTab === 'sinistres') exportSinistres(dataSin);
    if (activeTab === 'encaissements') exportEncaissements(dataEnc);
    if (activeTab === 'paiements') exportPaiements(dataPay);
    if (activeTab === 'cadence') exportCadence(dataCad);
    if (activeTab === 'cadence-enc') exportCadenceEncaissements(dataCadEnc);
  };

  const dataActive = activeTab === 'sinistres' ? dataSin
    : activeTab === 'encaissements' ? dataEnc
      : activeTab === 'paiements' ? dataPay
        : activeTab === 'cadence' ? dataCad
          : activeTab === 'cadence-enc' ? dataCadEnc
            : null;

  // Données exportables via TableExportButton (standard)
  const exportCfg = buildExportForTab(
    activeTab, dataSin, dataEnc, dataPay, dataCad, dataCadEnc, annee
  );
  const tabFilenameSuffix = {
    'sinistres': 'sinistres',
    'encaissements': 'encaissements',
    'paiements': 'paiements',
    'cadence': 'cadence-paiements',
    'cadence-enc': 'cadence-encaissements',
  }[activeTab] || 'export';

  const tabItems = [
    {
      key: 'sinistres',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <BarChartOutlined />
          {t('reporting.onglets.sinistres')}
          {loadingSin && <span style={{ fontSize: 10, color: MUTED }}> ...</span>}
        </span>
      ),
      children: <TabSinistres data={dataSin} loading={loadingSin} />,
    },
    {
      key: 'encaissements',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <DollarOutlined />
          {t('reporting.onglets.encaissements')}
          {loadingEnc && <span style={{ fontSize: 10, color: MUTED }}> ...</span>}
        </span>
      ),
      children: <TabEncaissements data={dataEnc} loading={loadingEnc} />,
    },
    {
      key: 'paiements',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <SendOutlined />
          {t('reporting.onglets.paiements')}
          {loadingPay && <span style={{ fontSize: 10, color: MUTED }}> ...</span>}
        </span>
      ),
      children: <TabPaiements data={dataPay} loading={loadingPay} />,
    },
    {
      key: 'cadence',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <BarChartOutlined style={{ transform: 'rotate(90deg)' }} />
          {t('reporting.onglets.cadencePaiements')}
          {loadingCad && <span style={{ fontSize: 10, color: MUTED }}> ...</span>}
        </span>
      ),
      children: <TabCadence data={dataCad} loading={loadingCad} />,
    },
    {
      key: 'cadence-enc',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <DollarOutlined style={{ transform: 'rotate(90deg)' }} />
          {t('reporting.onglets.cadenceEncaissements')}
          {loadingCadEnc && <span style={{ fontSize: 10, color: MUTED }}> ...</span>}
        </span>
      ),
      children: <TabCadenceEncaissements data={dataCadEnc} loading={loadingCadEnc} />,
    },
  ];

  return (
    <Layout title={t('reporting.titreOnglet')}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        .rpt-page * { font-family: 'DM Sans', sans-serif; }
        .rpt-page .ant-tabs-nav { margin-bottom: 0 !important; }
        .rpt-page .ant-tabs-tab { padding: 10px 18px !important; }
        .rpt-page .ant-tabs-content-holder { padding-top: 0; }
        @media print { .no-print { display:none!important; } }
      `}</style>

      <div className="rpt-page" style={{ maxWidth: 1500 }}>

        {/* Pas de `compte` : les cinq onglets (sinistres, encaissements, paiements,
            cadences) affichent des tableaux de natures differentes, sans element
            commun a denombrer. */}
        <PageHeader
          titre={t('reporting.titre')}
          icone={<BarChartOutlined />}
          actions={(
            <div className="no-print"
              style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <Select value={mois} onChange={setMoisM} style={{ width: 130 }}
                options={MOIS_FR.slice(1).map((m, i) => ({ value: i + 1, label: m }))} />
              <Select value={annee} onChange={setAnneeM} style={{ width: 100 }}
                options={ANNEES.map(a => ({ value: a, label: String(a) }))} />
              <Tooltip title={t('reporting.actualiserOnglet')}>
                <button onClick={actualiser} style={{
                  border: `1px solid ${BORDER}`,
                  borderRadius: 8, padding: '7px 12px', background: WHITE,
                  cursor: 'pointer', color: MUTED
                }}>
                  <ReloadOutlined />
                </button>
              </Tooltip>
              <button onClick={exporter} disabled={!dataActive} style={{
                border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13,
                background: dataActive ? G : BORDER, color: WHITE, fontWeight: 600,
                cursor: dataActive ? 'pointer' : 'not-allowed',
                display: 'flex', gap: 6, alignItems: 'center'
              }}>
                <DownloadOutlined /> {t('reporting.excelDetaille')}
              </button>
              <TableExportButton
                floating
                data={exportCfg.data}
                columns={exportCfg.columns}
                filename={`reporting-mensuel-${tabFilenameSuffix}-${MOIS_FR[mois].toLowerCase()}-${annee}`}
                title={`${exportCfg.title} · ${MOIS_FR[mois]} ${annee}`}
                subtitle={t('reporting.export.sousTitre', { mois: MOIS_FR[mois], annee, anneePrecedente: annee - 1 })}
                logo={logoLocal}
                showTotals={false}
                disabled={!dataActive || exportCfg.data.length === 0}
              />
              <button onClick={() => window.print()} disabled={!dataActive} style={{
                border: `1px solid ${BORDER}`, borderRadius: 8, padding: '8px 16px',
                fontSize: 13, background: WHITE, color: INK, fontWeight: 600,
                cursor: dataActive ? 'pointer' : 'not-allowed',
                display: 'flex', gap: 6, alignItems: 'center'
              }}>
                <PrinterOutlined /> {t('reporting.imprimer')}
              </button>
            </div>
          )}
        />

        {/* ── Tabs ── */}
        <Tabs
          activeKey={activeTab}
          onChange={onTabChange}
          items={tabItems}
          type="card"
          size="middle"
        />
      </div>
    </Layout>
  );
}
